import { useMemo, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type ProfessionalOption = { id: string; name: string };

interface ProfessionalComboboxProps {
  professionals: ProfessionalOption[];
  /** "ALL" or a professional id. */
  value: string;
  onSelect: (value: string) => void;
  disabled?: boolean;
  /** Label for the ALL sentinel option and the trigger when nothing is selected. */
  allLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
}

/* Popover-based combobox instead of a Radix Select. A search input embedded in
   SelectContent loses focus to the Select's typeahead/focus management every
   time the filtered list re-renders, which swallows fast keystrokes. A plain
   Popover has neither behavior, so the input keeps focus no matter how fast
   the user types. */
export function ProfessionalCombobox({
  professionals,
  value,
  onSelect,
  disabled,
  allLabel = "All Professionals",
  placeholder = "Select professional",
  searchPlaceholder = "Search professionals...",
}: ProfessionalComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const options = useMemo(() => {
    const sorted = [...professionals].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    if (!search.trim()) {
      return sorted;
    }
    const searchLower = search.toLowerCase();
    return sorted.filter((prof) => prof.name.toLowerCase().includes(searchLower));
  }, [professionals, search]);

  const selected = professionals.find(
    (professional) => professional.id === value,
  );

  const select = (next: string) => {
    onSelect(next);
    setSearch(""); // Clear search on selection
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch(""); // reopen with the full list
      }}
    >
      {/* The clear-X is a sibling of the trigger (overlaid on its right edge),
          not a child — a button can't nest inside the trigger button, and as a
          real <button> it stays keyboard focusable (Tab + Enter/Space) */}
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between bg-white text-gray-900 border-gray-300 hover:bg-gray-50 font-normal"
          >
            <span
              className={cn(
                "truncate",
                // keep the label clear of the overlaid X
                value !== "ALL" && "mr-6",
              )}
            >
              {value === "ALL" ? allLabel : selected?.name || placeholder}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        {value !== "ALL" && (
          <button
            type="button"
            aria-label="Clear professional filter"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => select("ALL")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        {/* Plain buttons instead of SelectItem so the list carries no
            typeahead/focus behavior of its own; listbox/option roles complete
            the combobox contract the trigger advertises */}
        <div
          role="listbox"
          aria-label="Professionals"
          className="max-h-[300px] overflow-y-auto p-1"
        >
          {/* The ALL sentinel isn't part of the searched data, so hide it while
              a query is typed — otherwise it renders above (and contradicts)
              the "No professionals found" empty state */}
          {!search.trim() && (
            <button
              type="button"
              role="option"
              aria-selected={value === "ALL"}
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
              onClick={() => select("ALL")}
            >
              <Check
                className={cn(
                  "absolute left-2 h-4 w-4",
                  value === "ALL" ? "opacity-100" : "opacity-0",
                )}
              />
              {allLabel}
            </button>
          )}
          {options.length > 0 ? (
            options.map((professional) => (
              <button
                key={professional.id}
                type="button"
                role="option"
                aria-selected={value === professional.id}
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => select(professional.id)}
              >
                <Check
                  className={cn(
                    "absolute left-2 h-4 w-4",
                    value === professional.id ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="truncate">{professional.name}</span>
              </button>
            ))
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No professionals found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
